import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage, FormattedNumber } from "react-intl";
import Box from "@redbubble/design-system/react/Box";
import Text from "@redbubble/design-system/react/Text";
import {
  getInventoryItemBestVolumeDiscount,
  isDiscountCheaperThanOrEqualToVolumeDiscount,
} from "../../lib/volumeDiscounts";
import styles from "./styles.scss";
import { StrikethroughProductPrice } from "./StrikethroughProductPrice";
import { getPriceDiscountValuesFromInventoryItem } from "../../lib/inventory/discount";

const PriceDisplay = ({ discounted, children }) => {
  const className = discounted ? styles.discountedPrice : "";

  return (
    <Box className={className} data-testid="product-price">
      <Text display="block" type="display5">
        {children}
      </Text>
    </Box>
  );
};

const DiscountDisplay = ({ children }) => (
  <Box data-testid="product-discount">
    <Text muted display="block" type="body2">
      {children}
    </Text>
  </Box>
);

const ProductPrice = ({ inventoryItem, loading, allowEmptyPrice }) => {
  if (loading) {
    return (
      <Text display="block" type="display5" loading>
        00.00
      </Text>
    );
  }

  if (!inventoryItem) {
    return null;
  }

  const volumeDiscountPriceValues =
    getInventoryItemBestVolumeDiscount(inventoryItem); // aka volume discount pricing
  const inventoryPriceDiscount =
    getPriceDiscountValuesFromInventoryItem(inventoryItem); // promo discount pricing

  const promoDiscountCheaper = isDiscountCheaperThanOrEqualToVolumeDiscount(
    volumeDiscountPriceValues,
    inventoryPriceDiscount
  );

  if (inventoryPriceDiscount && promoDiscountCheaper) {
    return (
      <Box>
        <PriceDisplay discounted>
          <FormattedNumber
            value={inventoryPriceDiscount.discountedPrice}
            // eslint-disable-next-line react/style-prop-object
            style="currency"
            currency={inventoryItem.price.currency}
          />
        </PriceDisplay>
        <DiscountDisplay>
          <StrikethroughProductPrice
            price={inventoryItem.price.amount}
            currency={inventoryItem.price.currency}
            discountPercent={inventoryPriceDiscount.discountPercent}
          />
        </DiscountDisplay>
      </Box>
    );
  }
  if (volumeDiscountPriceValues) {
    return (
      <PriceDisplay>
        <FormattedMessage
          defaultMessage="From {amount}"
          values={{
            amount: (
              <FormattedNumber
                value={volumeDiscountPriceValues.price.amount}
                // eslint-disable-next-line react/style-prop-object
                style="currency"
                currency={volumeDiscountPriceValues.price.currency}
              />
            ),
            quantity: volumeDiscountPriceValues.quantity,
          }}
        />
      </PriceDisplay>
    );
  }
  if (inventoryItem.price) {
    return (
      <PriceDisplay>
        <FormattedNumber
          value={inventoryItem.price.amount}
          // eslint-disable-next-line react/style-prop-object
          style="currency"
          currency={inventoryItem.price.currency}
        />
      </PriceDisplay>
    );
  }

  if (allowEmptyPrice) {
    return <PriceDisplay>&nbsp;</PriceDisplay>;
  }

  return null;
};

PriceDisplay.propTypes = {
  children: PropTypes.node,
  discounted: PropTypes.bool,
};

DiscountDisplay.propTypes = {
  children: PropTypes.node,
};

DiscountDisplay.defaultProps = {
  children: null,
};

PriceDisplay.defaultProps = {
  children: null,
  discounted: false,
};

ProductPrice.propTypes = {
  loading: PropTypes.bool,
  allowEmptyPrice: PropTypes.bool,
  inventoryItem: PropTypes.shape({
    price: PropTypes.shape({
      amount: PropTypes.number,
      currency: PropTypes.string,
    }),
    volumeDiscount: PropTypes.shape({
      thresholds: PropTypes.arrayOf(
        PropTypes.shape({
          quantity: PropTypes.number,
          percentOff: PropTypes.number,
        })
      ),
    }),
  }),
};

ProductPrice.defaultProps = {
  loading: false,
  allowEmptyPrice: false,
  inventoryItem: null,
};

export default ProductPrice;
